// Self-hosted fonts (@fontsource, OFL). Nothing here talks to Google: EU visitors'
// IPs must not reach fonts.googleapis.com / fonts.gstatic.com.
//
// `importUrl` (brand.config, site_settings.appearance.fonts) is still the stored
// key. It may be:
//   - "local:Family One,Family Two"  -> the format we write now
//   - a legacy https://fonts.googleapis.com/css2?family=... URL (live DB rows) ->
//     mapped to the same local families; unknown families are dropped
//   - any other https URL -> loaded as-is (admin free-text escape hatch)

// Loaders must be literal dynamic imports so Vite bundles + code-splits each CSS
// file (and its woff2). Latin subset only; weights = union used by the old URLs.
const FAMILIES = {
  "Playfair Display": [
    () => import("@fontsource/playfair-display/latin-400.css"),
    () => import("@fontsource/playfair-display/latin-700.css"),
    () => import("@fontsource/playfair-display/latin-400-italic.css"),
  ],
  "IBM Plex Mono": [
    () => import("@fontsource/ibm-plex-mono/latin-400.css"),
    () => import("@fontsource/ibm-plex-mono/latin-500.css"),
  ],
  "Inter": [
    () => import("@fontsource/inter/latin-300.css"),
    () => import("@fontsource/inter/latin-400.css"),
    () => import("@fontsource/inter/latin-500.css"),
    () => import("@fontsource/inter/latin-600.css"),
    () => import("@fontsource/inter/latin-700.css"),
  ],
  "DM Serif Display": [
    () => import("@fontsource/dm-serif-display/latin-400.css"),
    () => import("@fontsource/dm-serif-display/latin-400-italic.css"),
  ],
  "Lato": [
    () => import("@fontsource/lato/latin-300.css"),
    () => import("@fontsource/lato/latin-400.css"),
    () => import("@fontsource/lato/latin-700.css"),
  ],
  "Fira Code": [
    () => import("@fontsource/fira-code/latin-400.css"),
    () => import("@fontsource/fira-code/latin-500.css"),
  ],
  "Source Sans 3": [
    () => import("@fontsource/source-sans-3/latin-300.css"),
    () => import("@fontsource/source-sans-3/latin-400.css"),
    () => import("@fontsource/source-sans-3/latin-700.css"),
  ],
  "Space Mono": [
    () => import("@fontsource/space-mono/latin-400.css"),
  ],
};

const LOCAL_PREFIX = "local:";

export const localFontsUrl = (families) => LOCAL_PREFIX + families.join(",");

// Single source of truth for the admin presets (label -> families + CSS stacks).
export const FONT_PRESETS = [
  { label: "Classic", families: ["DM Serif Display", "IBM Plex Mono", "Lato"], display: "'DM Serif Display', serif", body: "'Lato', sans-serif", mono: "'IBM Plex Mono', monospace" },
  { label: "Modern", families: ["Inter", "Fira Code"], display: "'Inter', sans-serif", body: "'Inter', sans-serif", mono: "'Fira Code', monospace" },
  { label: "Editorial", families: ["Playfair Display", "Source Sans 3", "Space Mono"], display: "'Playfair Display', serif", body: "'Source Sans 3', sans-serif", mono: "'Space Mono', monospace" },
].map((p) => ({ ...p, importUrl: localFontsUrl(p.families) }));

// Returns { families: string[], external: string|null }.
export function resolveFontSource(importUrl) {
  const url = String(importUrl || "").trim();
  if (url.startsWith(LOCAL_PREFIX)) {
    return { families: url.slice(LOCAL_PREFIX.length).split(",").map((s) => s.trim()).filter(Boolean), external: null };
  }
  if (/^(https?:)?\/\/fonts\.(googleapis|gstatic)\.com\//i.test(url)) {
    let families = [];
    try {
      families = new URL(url.startsWith("//") ? "https:" + url : url).searchParams.getAll("family")
        .map((f) => f.split(":")[0].replace(/\+/g, " ").trim());
    } catch { /* malformed URL: no families */ }
    return { families, external: null };
  }
  return { families: [], external: url || null };
}

const loaded = new Set();

// Starts loading the bundled CSS for every known family; unknown ones are skipped.
export function loadLocalFonts(families) {
  for (const name of families) {
    const loaders = FAMILIES[name];
    if (!loaders || loaded.has(name)) continue;
    loaded.add(name);
    loaders.forEach((load) => load().catch(() => loaded.delete(name)));
  }
}

// Applies an importUrl; returns the external (non-Google) stylesheet URL, if any.
export function applyFontSource(importUrl) {
  const { families, external } = resolveFontSource(importUrl);
  loadLocalFonts(families);
  return external;
}
